import { findSkater, SKATERS, type SkaterProfile } from "../game/skaters";
import { STAT_IDS, STAT_INFO, type Stats } from "../game/stats";
import { StatPips } from "./StatPips";
import { PickList, type PickItem } from "./PickList";
import { LABEL, TITLE } from "./theme";

// Character select (#64): who you ride. The lobby's picker shows the whole
// roster as a list you tap or click (#225: no arrow buttons; ↑ ↓ step it
// when it has focus, and it scrolls for a roster of dozens); Pause > Skater lists them for the pad and shows the
// highlighted one's card. The model itself turns on a turntable behind
// either (Game.spinTurntable).

/** A skater's name, style and ten stat bars (their own sheet's when given, else their starting spread). */
export function SkaterCard({ skater, stats, narrow = false }: { skater: SkaterProfile; stats?: Readonly<Stats>; narrow?: boolean }) {
  const shown = stats ?? skater.stats;
  return (
    <div data-skater-card className="w-full">
      <p className={`${TITLE} text-center text-xl`}>{skater.name}</p>
      <p className={`${LABEL} mt-1 text-center`}>{skater.style}</p>
      <ul aria-label={`${skater.name}'s stats`} className={`mx-auto mt-2 grid max-w-[22rem] gap-x-3 gap-y-0.5 ${narrow ? "grid-cols-1 [@media(max-height:520px)]:hidden" : "grid-cols-2 lg:grid-cols-1"}`}>
        {STAT_IDS.map((id) => (
          <li key={id} className="flex items-center justify-between text-[0.65rem] font-bold uppercase">
            <span className="truncate">{STAT_INFO[id].label}</span>
            <StatPips id={id} value={shown[id]} base={skater.stats} small />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The card for `shown`, in a space as tall as the roster's tallest card
 * (#222): every card is laid out in one grid cell and all but one hidden, so
 * moving between skaters never changes the height and slides what's around
 * it (a centred menu's rows under a resting mouse, the picker's arrows).
 */
export function SkaterCards({ shown, riding, stats, narrow = false }: { shown: string; riding?: string; stats?: Readonly<Stats>; narrow?: boolean }) {
  return (
    <div className="grid">
      {SKATERS.map((s) => (
        <div key={s.id} data-skater-slot={s.id} aria-hidden={s.id === shown ? undefined : true} className={s.id === shown ? "[grid-area:1/1]" : "invisible [grid-area:1/1]"}>
          <SkaterCard skater={s} stats={s.id === riding ? stats : undefined} narrow={narrow} />
        </div>
      ))}
    </div>
  );
}

/** A skater's badge: a little head-and-shoulders roundel in their colours. */
function Badge({ skater }: { skater: SkaterProfile }) {
  const { hair, skin, top } = skater.look;
  const crown = hair.hat ?? hair.color;
  return (
    <span
      aria-hidden="true"
      className="block size-6 shrink-0 rounded-full border-2 border-ink"
      style={{ background: `linear-gradient(${crown} 0 36%, ${skin} 36% 64%, ${top.color} 64%)` }}
    />
  );
}

/**
 * The lobby's picker (#225 round 2): the roster as a list, the picked
 * skater's card beside it; ↑ ↓ step it when focused. `extra` pads the list
 * (the dev fixture's fake skaters); picking one of those does nothing.
 */
export function SkaterPicker({ skaterId, onPick, extra = [] }: { skaterId: string; onPick: (id: string) => void; extra?: readonly PickItem[] }) {
  const skater = findSkater(skaterId);
  const items: PickItem[] = [...SKATERS.map((s) => ({ id: s.id, name: s.name, lead: <Badge skater={s} /> })), ...extra];
  return (
    <PickList
      label="Skater"
      data-skater-picker
      items={items}
      rows={6}
      picked={skater.id}
      onPick={(id) => SKATERS.some((s) => s.id === id) && onPick(id)}
      aside={
        <div aria-live="polite">
          {/* The lobby's side column (#174): no stats on a phone on its side, where the height's needed. */}
          <SkaterCards shown={skater.id} narrow />
        </div>
      }
    />
  );
}
